import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { Phone, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Markdown } from '@/components/ui/markdown'
import { NotFoundContent } from '@/components/NotFoundContent'
import { allPosts } from 'content-collections'
import { generateSeo } from '@/lib/seo'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_public/blog/$slug')({
  staticData: {
    sitemap: {
      priority: '0.6',
      changefreq: 'monthly',
      entries: allPosts.map((p) => ({
        path: `/blog/${p.slug}`,
        lastmod: p.publishedAt,
      })),
    },
  },
  loader: ({ params }) => {
    const post = allPosts.find((p) => p.slug === params.slug)
    if (!post) throw notFound()
    return post
  },
  head: ({ loaderData: post, match }) => {
    if (!post) return { meta: [], links: [] }
    const { meta, links } = generateSeo({
      title: `${post.title} — PhoneSalad`,
      description: post.description,
      keywords: post.keywords,
      pathname: match.pathname,
      type: 'article',
    })

    return {
      meta: [
        ...meta,
        { property: 'article:published_time', content: post.publishedAt },
      ],
      links,
    }
  },
  component: BlogArticlePage,
  notFoundComponent: NotFoundContent,
})

function formatDate(dateStr: string) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function BlogArticlePage() {
  const post = Route.useLoaderData()
  const { t } = useTranslation(['marketing', 'common'])

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 pt-20">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <Link
                to="/blog"
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('blog.backToBlog')}
              </Link>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="bg-primary/10 text-primary px-2.5 py-0.5 rounded-full font-medium">
                  {post.category}
                </span>
                <span>{formatDate(post.publishedAt)}</span>
                <span>·</span>
                <span>{post.readingTime}</span>
              </div>
            </div>
            
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <Markdown content={post.content} />
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-background">
        <div className="container mx-auto px-4 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-xl border bg-primary/5 p-8 md:p-12 text-center">
              <Phone className="w-10 h-10 text-primary mx-auto mb-4" />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                {t('blog.ctaTitle')}
              </h2>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                {t('blog.ctaDescription')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button size="lg" asChild>
                  <Link to="/dialer">{t('navigation.getStarted')}</Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/pricing">{t('navigation.pricing')}</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
